import { z } from "zod"
import { clientActionFromError } from "@/entities/subscription"
import { getStoredUserId } from "@/shared/lib/auth"
import { captureError } from "@/shared/lib/diagnostics"
import {
	RuStorePay,
	type RuStorePayErrorCode,
	type RuStorePurchaseAvailability,
	type RuStorePurchaseErrorData,
	type RuStoreUserAuthorizationStatus,
} from "@/shared/lib/rustore-pay/plugin"
import { createDeviceStorageItem } from "@/shared/lib/storage"
import type { PlanId } from "../../model/plans"
import { PLAN_CATALOG, PLAN_ORDER } from "../../model/plans"
import type { ProductInfo, PurchaseAttempt } from "../../model/types"
import {
	isUserCanceled,
	PurchaseUnavailableError,
	type PurchaseUnavailableReason,
	wrapStep,
} from "../errors"
import { stripZeroFraction } from "../format-price"
import type { ProcessSummary, StoreTransaction } from "../process-transaction"
import { processTransaction, processTransactions } from "../process-transaction"

const pendingPurchaseSchema = z.object({
	id: z.string(),
	productId: z.string(),
	userId: z.int(),
})

type PendingPurchase = z.infer<typeof pendingPurchaseSchema>

// Device scope: a paid purchase that the backend did not verify yet must
// survive logout, token expiry and an account change. The userId in each
// record keeps the recovery per account.
const pendingPurchases = createDeviceStorageItem<PendingPurchase[]>({
	key: "rustore-pending-purchases",
	schema: z.array(pendingPurchaseSchema),
	fallback: [],
})

function readPending(): PendingPurchase[] {
	return pendingPurchases.get()
}

function writePending(purchases: PendingPurchase[]): void {
	pendingPurchases.set(purchases)
}

// Builds before the device scope kept this list in a plain key, which
// clearSession removed. Boot calls this before any clearSession can run.
const LEGACY_PENDING_KEY = "rustore_pending_purchases"

export function migrateLegacyPendingPurchases(): void {
	try {
		const raw = localStorage.getItem(LEGACY_PENDING_KEY)
		if (raw === null) return
		const parsed: unknown = JSON.parse(raw)
		let legacy: PendingPurchase[] = []
		if (Array.isArray(parsed)) {
			legacy = parsed.flatMap((entry) => {
				const result = pendingPurchaseSchema.safeParse(entry)
				if (result.success) return [result.data]
				return []
			})
		}
		const current = readPending()
		const known = new Set(current.map((purchase) => purchase.id))
		const merged = [
			...current,
			...legacy.filter((purchase) => {
				if (known.has(purchase.id)) return false
				known.add(purchase.id)
				return true
			}),
		]
		// Keep the legacy key if the write fails, so the next launch tries again.
		if (pendingPurchases.set(merged))
			localStorage.removeItem(LEGACY_PENDING_KEY)
	} catch (error) {
		captureError(error, { scope: "rustore.migrate-pending" })
	}
}

export function getUnfinishedTransactions(userId: number): StoreTransaction[] {
	return readPending().filter((purchase) => purchase.userId === userId)
}

export const finishTransaction = async (
	transactionId: string,
): Promise<void> => {
	writePending(readPending().filter((p) => p.id !== transactionId))
	await RuStorePay.updateAcknowledgementState({
		purchaseId: transactionId,
		state: "ACKNOWLEDGED",
	})
}

export async function getProducts(): Promise<ProductInfo[]> {
	const { products } = await wrapStep("rustore/getProducts", () =>
		RuStorePay.getProducts({
			productIds: PLAN_ORDER.map((planId) => PLAN_CATALOG[planId].rustoreId),
		}),
	)
	const byId = new Map(products.map((product) => [product.productId, product]))

	const loaded = PLAN_ORDER.map((planId): ProductInfo | null => {
		const { rustoreId } = PLAN_CATALOG[planId]
		const product = byId.get(rustoreId)
		if (!product) {
			console.error("[rustore] getProducts returned no product:", rustoreId)
			return null
		}
		return {
			planId,
			id: product.productId,
			displayName: product.title,
			displayPrice: stripZeroFraction(product.amountLabel),
			currencyCode: product.currency,
		} satisfies ProductInfo
	}).filter((p): p is ProductInfo => p !== null)

	if (loaded.length < PLAN_ORDER.length) {
		throw new Error(
			`[rustore] loaded ${loaded.length}/${PLAN_ORDER.length} products`,
		)
	}
	return loaded
}

export async function purchaseProduct(
	planId: PlanId,
): Promise<PurchaseAttempt> {
	const { rustoreId } = PLAN_CATALOG[planId]

	// The backend refuses a purchase whose `externalAccountId` is missing, so
	// opening the payment sheet without one would charge for access it can
	// never grant.
	const userId = await getStoredUserId()
	if (userId === null) {
		throw new Error("[rustore] no user id for appUserId — purchase aborted")
	}

	await assertPurchaseAvailable()

	const { status, ...transaction } = await pay(rustoreId, userId)
	writePending([...readPending(), { ...transaction, userId }])

	const outcome = await processTransaction(
		"rustore",
		transaction,
		"purchase",
		finishTransaction,
	)
	if (outcome.status === "failed") {
		// The payment is in progress, and the backend keeps the record open
		// (`client_action: "none"`). The entry stays in the stored list, and the
		// recovery sends it again. Thus the UI shows "payment is processing" and
		// not a failure.
		if (
			status === "PROCESSING" &&
			clientActionFromError(outcome.error) === "none"
		) {
			throw Object.assign(
				new Error("[rustore] payment is processing", {
					cause: outcome.error,
				}),
				{ code: "PENDING" },
			)
		}
		throw outcome.error
	}

	return { processed: true, subscription: outcome.subscription }
}

// These purchase statuses can still give access to the subscription. `ACTIVE`
// means that the user paid. `PROCESSING` means that the payment is in progress.
// Until the payment ends, the backend gives `payment_pending`, and the recovery
// sends the purchase again. `INVOICE_CREATED` has no payment, thus it is not in
// this set.
const CONTINUE_STATUSES = new Set(["ACTIVE", "PROCESSING"])

type PaidTransaction = { id: string; productId: string; status?: string }

async function pay(
	productId: string,
	userId: number,
): Promise<PaidTransaction> {
	try {
		const result = await wrapStep("rustore/purchase", () =>
			RuStorePay.purchase({ productId, appUserId: String(userId) }),
		)
		return { id: result.purchaseId, productId: result.productId }
	} catch (error) {
		// The payment sheet closed before the result (`USER_CANCELED`), or the SDK
		// gave a failure (`PURCHASE_FAILED` or a RuStore core code). If the SDK has
		// the purchase ID, read the status from the store. A bank app can complete
		// the payment after the sheet closes.
		const native = nativeError(error)
		const purchaseId = native.data?.purchaseId
		if (!purchaseId) throw unavailableError(native.code, error) ?? error

		const purchase = await wrapStep("rustore/getPurchase", () =>
			RuStorePay.getPurchase({ purchaseId }),
		)
		if (!CONTINUE_STATUSES.has(purchase.status) || !purchase.productId)
			throw error
		return {
			id: purchaseId,
			productId: purchase.productId,
			status: purchase.status,
		}
	}
}

type NativeError = {
	code?: RuStorePayErrorCode
	data?: RuStorePurchaseErrorData
}

// `wrapStep` does not wrap `USER_CANCELED`. It wraps the other codes, thus
// their native error is in `cause`.
function nativeError(error: unknown): NativeError {
	let native: unknown
	if (isUserCanceled(error)) {
		native = error
	} else {
		native = (error as { cause?: unknown } | null)?.cause
	}
	if (!native || typeof native !== "object") return {}
	const { code, data } = native as { code?: unknown; data?: unknown }
	const result: NativeError = {}
	if (typeof code === "string") result.code = code as RuStorePayErrorCode
	if (data && typeof data === "object") {
		const { purchaseId } = data as { purchaseId?: unknown }
		if (typeof purchaseId === "string" && purchaseId) {
			result.data = { ...(data as RuStorePurchaseErrorData), purchaseId }
		}
	}
	return result
}

// For these core reasons, the SDK can give `Unavailable`. But the user can
// still sign in with VK ID in the payment sheet, without the RuStore app. Thus
// do not stop the purchase for these reasons.
const NON_BLOCKING_REASONS = new Set([
	"RUSTORE_NOT_INSTALLED",
	"RUSTORE_USER_UNAUTHORIZED",
])

const UNAVAILABLE_REASONS: Record<string, PurchaseUnavailableReason> = {
	RUSTORE_APP_BANNED: "app-banned",
	RUSTORE_USER_BANNED: "user-banned",
	RUSTORE_OUTDATED: "store-outdated",
}

function unavailableError(
	code: string | undefined,
	cause: unknown,
): PurchaseUnavailableError | null {
	if (!code) return null
	const reason = UNAVAILABLE_REASONS[code]
	if (!reason) return null
	return new PurchaseUnavailableError(reason, cause)
}

async function assertPurchaseAvailable(): Promise<void> {
	let availability: RuStorePurchaseAvailability
	try {
		availability = await RuStorePay.getPurchaseAvailability()
	} catch (error) {
		// The availability check failed, for example because of the network or an
		// old binary. Then the purchase call gives its error, thus do not stop the
		// purchase here.
		captureError(error, { scope: "rustore.availability" })
		return
	}
	if (availability.available) return

	const { reasonCode, cause } = availability
	if (reasonCode && NON_BLOCKING_REASONS.has(reasonCode)) return
	// A binary before the `cause` field gives no reason. Do not stop the purchase
	// for this binary.
	if (!reasonCode && !cause) return

	throw (
		unavailableError(reasonCode, availability) ??
		new PurchaseUnavailableError("unknown", availability)
	)
}

async function readAuthorizationStatus(): Promise<RuStoreUserAuthorizationStatus | null> {
	try {
		const { status } = await RuStorePay.getUserAuthorizationStatus()
		if (status === "AUTHORIZED" || status === "UNAUTHORIZED") return status
		return null
	} catch (error) {
		// An old binary rejects the call with `UNIMPLEMENTED`. Thus the status is
		// unknown, and the app does not capture this error.
		const { code } = (error ?? {}) as { code?: unknown }
		if (code !== "UNIMPLEMENTED")
			captureError(error, { scope: "rustore.authorization-status" })
		return null
	}
}

export type RuStoreRestoreSummary = ProcessSummary & {
	/** RuStore has no signed-in user, thus `getPurchases` gives only the purchases of this device. */
	storeUnauthorized: boolean
}

export async function restoreTransactions(): Promise<RuStoreRestoreSummary> {
	const storeUnauthorized = (await readAuthorizationStatus()) === "UNAUTHORIZED"

	const { purchases } = await wrapStep("rustore/getPurchases", () =>
		RuStorePay.getPurchases({ productType: "SUBSCRIPTION" }),
	)

	const transactions = purchases.map((purchase) => {
		const transaction: StoreTransaction = { id: purchase.purchaseId }
		if (purchase.productId !== undefined) {
			transaction.productId = purchase.productId
		}
		return transaction
	})

	const summary = await processTransactions(
		"rustore",
		transactions,
		"restore",
		finishTransaction,
	)
	return { ...summary, storeUnauthorized }
}
